/*
 * ImageCropUpload — circular image upload + pan/zoom crop widget.
 *
 * One props-driven component shared between Team Branding (logo) and the
 * Profile screen (avatar). It owns its own file picker, drag/zoom
 * workspace, and crop pipeline. The result is a 512×512 JPEG data URL
 * handed back via onChange.
 *
 * States:
 *   - empty:    big dashed "Upload" drop target
 *   - editing:  workspace + zoom slider + Cancel / Use This Crop
 *   - preview:  current image with Replace / Remove
 */

import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Upload, Trash2, RefreshCw } from 'lucide-react';

const OUTPUT_SIZE = 512;
const WORKSPACE = 280;
const MAX_FILE_BYTES = 8 * 1024 * 1024;

interface Props {
  value: string;
  onChange: (s: string) => void;
  /** Customize the empty-state CTA label, e.g. "Upload logo" or "Upload photo". */
  emptyLabel?: string;
  /** Customize the preview image's accessible label. */
  alt?: string;
}

export function ImageCropUpload({ value, onChange, emptyLabel = 'Upload image', alt = 'Image' }: Props) {
  const [src, setSrc] = useState<string | null>(null);
  const [imgSize, setImgSize] = useState<{ w: number; h: number } | null>(null);

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const fileRef = useRef<HTMLInputElement | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number } | null>(null);

  const baseScale = imgSize
    ? Math.max(WORKSPACE / imgSize.w, WORKSPACE / imgSize.h)
    : 1;
  const drawnW = imgSize ? imgSize.w * baseScale * zoom : 0;
  const drawnH = imgSize ? imgSize.h * baseScale * zoom : 0;

  const clampPan = useCallback((x: number, y: number) => {
    if (!imgSize) return { x: 0, y: 0 };
    const minX = WORKSPACE - drawnW;
    const minY = WORKSPACE - drawnH;
    return {
      x: Math.min(0, Math.max(minX, x)),
      y: Math.min(0, Math.max(minY, y)),
    };
  }, [drawnW, drawnH, imgSize]);

  function handleFile(file: File) {
    setErr(null);
    if (!file.type.startsWith('image/')) {
      setErr('That file is not an image. Try a PNG, JPG, or HEIC.');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setErr(`Image is larger than ${Math.round(MAX_FILE_BYTES / (1024 * 1024))}MB. Pick a smaller one.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const img = new Image();
      img.onload = () => {
        setSrc(dataUrl);
        setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
        setZoom(1);
        const drawnWAtFit = img.naturalWidth * Math.max(WORKSPACE / img.naturalWidth, WORKSPACE / img.naturalHeight);
        const drawnHAtFit = img.naturalHeight * Math.max(WORKSPACE / img.naturalWidth, WORKSPACE / img.naturalHeight);
        setPan({
          x: (WORKSPACE - drawnWAtFit) / 2,
          y: (WORKSPACE - drawnHAtFit) / 2,
        });
      };
      img.onerror = () => setErr("That image didn't load. Try a different file.");
      img.src = dataUrl;
    };
    reader.onerror = () => setErr("Couldn't read that file.");
    reader.readAsDataURL(file);
  }

  useEffect(() => {
    if (imgSize) {
      setPan((p) => {
        const minX = WORKSPACE - imgSize.w * baseScale * zoom;
        const minY = WORKSPACE - imgSize.h * baseScale * zoom;
        return {
          x: Math.min(0, Math.max(minX, p.x)),
          y: Math.min(0, Math.max(minY, p.y)),
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, imgSize, baseScale]);

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (!imgSize) return;
    (e.target as Element).setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseX: pan.x,
      baseY: pan.y,
    };
  }
  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    const d = dragRef.current;
    if (!d) return;
    const nx = d.baseX + (e.clientX - d.startX);
    const ny = d.baseY + (e.clientY - d.startY);
    setPan(clampPan(nx, ny));
  }
  function onPointerUp() {
    dragRef.current = null;
  }

  function commitCrop() {
    if (!src || !imgSize) return;
    setBusy(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext('2d')!;
      const img = new Image();
      img.onload = () => {
        const ratio = OUTPUT_SIZE / WORKSPACE;
        ctx.fillStyle = '#0a0a0a';
        ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
        ctx.drawImage(
          img,
          pan.x * ratio,
          pan.y * ratio,
          drawnW * ratio,
          drawnH * ratio,
        );
        const out = canvas.toDataURL('image/jpeg', 0.9);
        onChange(out);
        setSrc(null);
        setImgSize(null);
        setBusy(false);
      };
      img.onerror = () => { setErr('Could not render the crop.'); setBusy(false); };
      img.src = src;
    } catch (e: any) {
      setErr(e?.message ?? 'Crop failed.');
      setBusy(false);
    }
  }

  function cancelCrop() {
    setSrc(null);
    setImgSize(null);
    setErr(null);
  }

  function clearImage() {
    onChange('');
    setSrc(null);
    setImgSize(null);
    setErr(null);
  }

  function chooseFile() {
    fileRef.current?.click();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = '';
        }}
      />

      {err && (
        <div className="t-hint" style={{ color: 'var(--text)', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid var(--red)', padding: 8, borderRadius: 6 }}>
          {err}
        </div>
      )}

      {src && imgSize ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <div
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            style={{
              position: 'relative',
              width: WORKSPACE,
              height: WORKSPACE,
              borderRadius: WORKSPACE / 2,
              overflow: 'hidden',
              background: '#0a0a0a',
              border: '2px solid var(--border)',
              cursor: dragRef.current ? 'grabbing' : 'grab',
              touchAction: 'none',
              userSelect: 'none',
            }}
          >
            <img
              src={src}
              alt=""
              draggable={false}
              style={{
                position: 'absolute',
                left: pan.x,
                top: pan.y,
                width: drawnW,
                height: drawnH,
                pointerEvents: 'none',
                userSelect: 'none',
                maxWidth: 'none',
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', maxWidth: WORKSPACE }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-mut)', letterSpacing: 0.6 }}>ZOOM</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              style={{ flex: 1, accentColor: 'var(--green-bright)' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="t-btn t-btn-ghost" onClick={cancelCrop} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="t-btn t-btn-primary" onClick={commitCrop} disabled={busy}>
              {busy ? 'Saving…' : 'Use This Crop'}
            </button>
          </div>

          <p className="t-hint" style={{ textAlign: 'center', margin: 0 }}>
            Drag the image to reposition. Use the slider to zoom in or out. What you see inside the circle is what will be saved.
          </p>
        </div>
      ) : value ? (
        // Preview row: flex-wraps when the screen can't fit the 88px
        // thumbnail + button column side-by-side so the buttons don't
        // crash into the thumbnail or fall off-screen.
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <img
            src={value}
            alt={alt}
            style={{
              width: 88, height: 88, borderRadius: 44,
              objectFit: 'cover', background: '#0a0a0a',
              border: '1px solid var(--border)',
              flexShrink: 0,
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button type="button" className="t-btn t-btn-ghost" onClick={chooseFile}>
              <RefreshCw width={14} height={14} /> Replace
            </button>
            <button type="button" className="t-btn t-btn-danger-ghost" onClick={clearImage}>
              <Trash2 width={14} height={14} /> Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={chooseFile}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 6, padding: 24, borderRadius: 12,
            background: 'var(--card)',
            border: '1.5px dashed var(--border)',
            cursor: 'pointer', color: 'var(--text-sec)',
            // Clamp to viewport so the dashed empty state never
            // overflows on phones; the WORKSPACE+80 ideal width
            // (360px) was wider than a 320px iPhone SE content area.
            width: '100%', maxWidth: 'min(100%, ' + (WORKSPACE + 80) + 'px)',
          }}
        >
          <Upload width={22} height={22} />
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{emptyLabel}</span>
          <span style={{ fontSize: 11, color: 'var(--text-mut)' }}>PNG, JPG, or HEIC up to 8MB</span>
        </button>
      )}
    </div>
  );
}
